<template>
  <div>
    <div class="wrapper">
      <md-field md-inline>
        <md-input
          ref="input"
          v-model="keyword"
          @input="inputChange"
          @keydown.native.enter="onSearch"
          @keydown.native.stop.esc="clear"
          @keyup.native.stop.arrow-down="focusResult"
          @compositionstart="IME = true"
          @compositionend="IME = false"
          placeholder="地名、緯度経度を入力して検索"
        />
        <v-btn
          v-if="hasKeyword"
          :class="[buttonStyles]"
          @click="clear"
          icon
          flat
        >
          <md-icon :class="[iconStyles]">clear</md-icon>
        </v-btn>
        <v-btn
          :loading="loading"
          :class="[buttonStyles]"
          @click="onSearch"
          icon
          flat
        >
          <md-icon :class="[iconStyles, { 'md-primary': hasKeyword }]"
            >search</md-icon
          >
        </v-btn>
      </md-field>
    </div>
  </div>
</template>

<script>
import { css } from 'emotion'
import { mapActions, mapGetters } from 'vuex'
import { isNull, inRange } from 'lodash'

const minLngJp = 122.93
const maxLngJp = 153.99
const minLatJp = 20.42
const maxLatJp = 45.56

const latLngPattern = (() => {
  const latPattern = '\\d{2}(\\.\\d+)?'
  const lngPattern = '\\d{3}(\\.\\d+)?'
  const joinPattern = (pattern1, pattern2) =>
    `(${pattern1})[^0-9.]+(${pattern2})`

  return new RegExp(
    [
      joinPattern(latPattern, lngPattern),
      joinPattern(lngPattern, latPattern),
    ].join('|'),
    'mg',
  )
})()

export function parseLatLng(input) {
  const matches = [...input.matchAll(latLngPattern)]
  if (matches.length === 0) return null

  const match = matches[0]
  const lat = parseFloat(match[1] || match[7])
  const lng = parseFloat(match[3] || match[5])

  const latLngIsNotNull = () => !isNaN(lat) && !isNaN(lng)
  const latLngIsInRange = () =>
    inRange(lat, minLatJp, maxLatJp) && inRange(lng, minLngJp, maxLngJp)

  if (latLngIsNotNull() && latLngIsInRange()) return { lat, lng }
  return null
}

export default {
  name: 'SearchInput',
  data() {
    return {
      IME: false,
      loading: false,
      keyword: '',
    }
  },
  computed: {
    ...mapGetters('pointGroupOperation', ['searchQuery']),
    hasKeyword() {
      return this.keyword.length > 0
    },
    latLng() {
      return parseLatLng(this.keyword)
    },
    buttonStyles() {
      return css`
        margin: 0 !important;
        ${this.keyword.length > 0
          ? css`
              cursor: pointer;
            `
          : ''}
      `
    },
    iconStyles() {
      return css`
        width: 40px !important;
        height: 40px !important;
      `
    },
  },
  methods: {
    ...mapActions('pointGroupOperation', {
      updateSearchQuery: 'UPDATE_SEARCH_QUERY',
      search: 'SEARCH',
      clearSearch: 'CLEAR_SEARCH',
      focusSearchResultList: 'FOCUS_SEARCH_RESULT_LIST',
      focusOutResultList: 'FOCUS_OUT_SEARCH_RESULT_LIST',
      addNewPointByLatLng: 'ADD_NEW_POINT_BY_LAT_LNG',
    }),
    async onSearch() {
      this.focusOutResultList()

      if (this.IME) return
      if (!isNull(this.latLng)) return this.addNewPoint()
      try {
        this.loading = true
        await this.search({ query: this.keyword })
      } catch (e) {
        return Promise.reject(e)
      } finally {
        this.loading = false
        this.$emit('complete-search')
      }
    },
    inputChange(input) {
      if (!input) this.clearSearch()
      this.$emit('change-keyword', input)
    },
    clear() {
      this.keyword = ''
      this.clearSearch()
    },
    focusResult() {
      if (this.IME) return
      this.focusSearchResultList()
      this.$refs.input.$el.blur()
    },
    focusInput() {
      this.$refs.input.$el.focus()
    },
    async addNewPoint() {
      const { lat, lng } = this.latLng
      try {
        this.loading = true
        await this.addNewPointByLatLng({ lat, lng })
        this.clearSearch()
      } catch (e) {
        return Promise.reject(e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.md-field {
  padding: 0 10px;
  margin: 0;
  min-height: 0;
}

.md-input {
  height: 40px;
}
</style>
